import type { CustomerProfile } from "@tdelivr/api-client/generated/customer/customer-profile.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, asMonth, type BoardRow } from "./board.ts";

/**
 * What the customers board does to the profiles CUS-01 returns, away from any rendering.
 *
 * **Two scopes, and this is the one page of the fifteen whose listing cost a scope nothing else
 * needed.** `customer-profile:read` is an optional client scope on `tdelivr-customer-app` — a
 * rider's own phone, which holds it to read its own profile. A roster granted to every holder of it
 * would hand a phone every customer's name in the tenant, and the only thing that had been
 * preventing that was the need to guess a v4 UUID, which ADR 0025 called "not an authorization
 * model; it is a hope". So the route requires `customer-profile:roster` in addition, which no
 * mobile client holds, and `navigation.ts` asks for both. The reasoning is DRV-01's
 * `driver-profile:workload` on the same shape of route, and the opposite conclusion to ADR 0027 §2
 * for the reason that ADR itself gives.
 *
 * **`Phone` has no field anywhere, and the design masks it.** The grid is
 * `Customer · Phone · Joined` with `+254 7•• ••• 421` in the middle column. CUS-01 holds an
 * `identitySubjectId`, a `displayName`, a `locale` and a `status`. Searched across every published
 * contract rather than only this one, nothing reachable publishes a customer's telephone number or
 * email address under any name. Q-103, and the decision recorded there is that the column carries
 * the absence **rather than a masked blank**: `+254 7•• ••• •••` would read as "we hold it and are
 * hiding it", which is the one wrong answer here.
 *
 * **The design's own sentence about masking and auditing is not true of this build, and it is a
 * safety claim.** "Personal data is masked until you open a record, and opening it is audited."
 * There is nothing to mask — the roster publishes a display name, which is the name itself. And
 * opening a record is **not** audited: CUS-01 writes its `protected_audit_log` from
 * `recordSideEffects`, which runs inside `update`. A change is audited; a read is not, and the
 * listing certainly is not. Shipping that sentence would promise an administrator a protection they
 * do not have, which is the brand board's Q-35 in a worse direction. Q-107.
 */

/**
 * The state class and the word.
 *
 * CUS-01's six statuses, each with the class the design uses for that kind of row, and the
 * contract's own word on the badge.
 *
 *   - `active` is `done`.
 *   - `restricted` is `caution` — the class whose label is "Qualified": in use, with a condition.
 *   - `suspended` is `closed`.
 *   - `closure_pending` is `human`, and it is the one row on this board that tints. It is the
 *     design's `human · deletion requested`, and a customer who has asked to be deleted is why
 *     somebody opened this page.
 *   - `closed` is `closed`.
 *   - `merged` is `closed`.
 *
 * **The design's `30-day clock` is not here.** Its closing note reads "Deletion requests run on a
 * legal clock; the row shows the days left", and CUS-01 publishes `updatedAt` and no deadline of
 * any kind — no statutory period, no due date, no jurisdiction. A row counting down from
 * `updatedAt` would be this console inventing a legal deadline, which is the worst class of
 * invented field. Q-107.
 */
export function stateOf(profile: CustomerProfile): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  switch (profile.status) {
    case "active":
      return { classKey: "done", label: "active" };
    case "restricted":
      return {
        classKey: "caution",
        label: "restricted",
        title: "CUS-01 holds this profile as restricted. What it restricts is the service's own.",
      };
    case "suspended":
      return { classKey: "closed", label: "suspended" };
    case "closure_pending":
      return {
        classKey: "human",
        label: "closure pending",
        title:
          "A closure has been asked for and not completed. CUS-01 publishes no deadline, so this row cannot say how long is left. Q-107.",
      };
    case "closed":
      return { classKey: "closed", label: "closed" };
    case "merged":
      return { classKey: "closed", label: "merged" };
  }
}

/** One row of the customers board. */
export function profileRow(profile: CustomerProfile): BoardRow {
  const state = stateOf(profile);
  return {
    id: profile.id,
    cells: [
      { text: profile.displayName },
      {
        text: "",
        absent: true,
        title:
          "Nothing reachable on this platform publishes a customer's telephone number or email address, under any name. A masked number here would read as a number this console is hiding. Q-103.",
      },
      // The design's `Joined` column: `Mar 2024`, `Sep 2026`. A month rather than a day, which is
      // the design's own grammar for this column and the only one of these five pages that uses it.
      { text: asMonth(profile.createdAt) },
    ],
    state,
    ...(state.classKey === "human" ? { tinted: true } : {}),
    /*
     * One line where the design's panel lists three, and it is the field Q-103 names as the lookup
     * that was missing alongside the roster: `identitySubjectId` is how a signed-in person is
     * resolved to a record at all, and two customers can share a display name. Q-108 records the
     * deviation; `locale`, the profile identifier and the tenant are left out.
     */
    detail: [{ label: "Identity subject", value: profile.identitySubjectId, mono: true }],
    history: [
      { label: "Created", value: asDay(profile.createdAt) },
      { label: "Last changed", value: asDay(profile.updatedAt) },
      { label: "Version", value: `v${String(profile.version)}` },
    ],
    glyph: "person_search",
  };
}

/**
 * The statuses the toolbar's filter offers, in CUS-01's own order.
 *
 * All six, because the listing publishes a `status` query parameter and answers any of them, so
 * choosing one refetches. The design's own filter options are derived from the states on its four
 * sample rows; this one is derived from what the operation accepts, which is the superset that does
 * not empty the board for a state the tenant happens not to have this week.
 */
export const PROFILE_STATUSES: readonly CustomerProfile["status"][] = [
  "active",
  "restricted",
  "suspended",
  "closure_pending",
  "closed",
  "merged",
];
